import { useState, useEffect, useMemo } from 'react'
import axios from 'axios'

const API_BASE = 'http://localhost:5016/api'

const TestApi = () => {
  // =====================
  // AUTH
  // =====================
  const [username, setUsername] = useState('admin')
  const [password, setPassword] = useState('admin123')
  const [token, setToken] = useState(localStorage.getItem('token'))
  const [role, setRole] = useState(null)
  const [error, setError] = useState(null)

  // =====================
  // USERS
  // =====================
  const [users, setUsers] = useState([])
  const [newUser, setNewUser] = useState({
    username: '',
    password: '',
    role: 'Admin'
  })

  // =====================
  // AXIOS INSTANCE (SAFE)
  // =====================
  const api = useMemo(() => {
    return axios.create({
      baseURL: API_BASE,
      headers: token ? { Authorization: `Bearer ${token}` } : {}
    })
  }, [token])

  // =====================
  // LOGIN
  // =====================
  const login = async () => {
    try {
      setError(null)

      const res = await axios.post(`${API_BASE}/auth/login`, {
        username,
        password
      })

      const jwt = res.data.token
      localStorage.setItem('token', jwt)
      setToken(jwt)

      const payload = JSON.parse(atob(jwt.split('.')[1]))
      setRole(payload.role)
    } catch {
      setError('Login failed')
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    setToken(null)
    setRole(null)
    setUsers([])
  }

  // =====================
  // LOAD USERS (GUARDED)
  // =====================
  const loadUsers = async () => {
    if (!token || role !== 'SuperAdmin') return

    try {
      const res = await api.get('/admin/users')
      setUsers(res.data)
    } catch {
      setError('Failed to load users')
    }
  }

  // =====================
  // CREATE USER
  // =====================
  const createUser = async () => {
    if (!newUser.username || !newUser.password) {
      setError('Username and password are required')
      return
    }

    try {
      setError(null)

      await api.post('/admin/users', {
        username: newUser.username,
        password: newUser.password,
        role: newUser.role
      })

      setNewUser({ username: '', password: '', role: 'Admin' })
      loadUsers()
    } catch (err) {
      setError(err.response?.data || 'Create user failed')
    }
  }

  // =====================
  // UPDATE USER
  // =====================
  const updateUser = async (id, role, isActive) => {
    await api.put(`/admin/users/${id}`, { role, isActive })
    loadUsers()
  }

  // =====================
  // DISABLE USER
  // =====================
  const disableUser = async (id) => {
    await api.delete(`/admin/users/${id}`)
    loadUsers()
  }

  // =====================
  // AUTO LOAD (SAFE)
  // =====================
  useEffect(() => {
    if (!token) return

    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      setRole(payload.role)

      if (payload.role === 'SuperAdmin') {
        loadUsers()
      }
    } catch {
      logout()
    }
  }, [token])

  // =====================
  // UI (TAILWIND)
  // =====================
  return (
    <div className="max-w-3xl mx-auto mt-10 px-4">
      <h2 className="text-2xl font-semibold mb-6">
        SuperAdmin – User Management
      </h2>

      {error && (
        <p className="mb-4 text-sm text-red-600">
          {error}
        </p>
      )}

      {/* LOGIN */}
      {!token && (
        <div className="space-y-3">
          <input
            className="w-full rounded border px-3 py-2 focus:outline-none focus:ring"
            placeholder="Username"
            value={username}
            onChange={e => setUsername(e.target.value)}
          />
          <input
            className="w-full rounded border px-3 py-2 focus:outline-none focus:ring"
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
          />
          <button
            className="w-full rounded bg-blue-600 py-2 text-white hover:bg-blue-700"
            onClick={login}
          >
            Login
          </button>
        </div>
      )}

      {/* DASHBOARD */}
      {token && (
        <>
          <div className="mt-6 border-t pt-4">
            <p className="text-sm mb-2">
              Logged as:{' '}
              <span className="font-semibold">{username}</span> | Role:{' '}
              <span className="font-semibold">{role}</span>
            </p>

            <button
              className="mb-4 rounded border border-red-500 px-3 py-1 text-sm text-red-600 hover:bg-red-50"
              onClick={logout}
            >
              Logout
            </button>

            {role !== 'SuperAdmin' && (
              <p className="text-yellow-600 text-sm">
                You are not allowed to manage users.
              </p>
            )}

            {role === 'SuperAdmin' && (
              <>
                {/* CREATE USER */}
                <h4 className="mt-6 mb-2 text-lg font-medium">
                  Create Admin
                </h4>

                <div className="space-y-3 mb-6">
                  <input
                    className="w-full rounded border px-3 py-2 focus:outline-none focus:ring"
                    placeholder="Username"
                    value={newUser.username}
                    onChange={e =>
                      setNewUser({ ...newUser, username: e.target.value })
                    }
                  />
                  <input
                    className="w-full rounded border px-3 py-2 focus:outline-none focus:ring"
                    type="password"
                    placeholder="Password"
                    value={newUser.password}
                    onChange={e =>
                      setNewUser({ ...newUser, password: e.target.value })
                    }
                  />
                  <select
                    className="w-full rounded border px-3 py-2 focus:outline-none focus:ring"
                    value={newUser.role}
                    onChange={e =>
                      setNewUser({ ...newUser, role: e.target.value })
                    }
                  >
                    <option value="Admin">Admin</option>
                    <option value="Editor">Editor</option>
                  </select>

                  <button
                    className="rounded bg-green-600 px-4 py-2 text-white hover:bg-green-700"
                    onClick={createUser}
                  >
                    Create
                  </button>
                </div>

                {/* USERS LIST */}
                <h4 className="mb-3 text-lg font-medium">
                  Admins
                </h4>

                <div className="space-y-3">
                  {users.map(u => (
                    <div
                      key={u.id}
                      className="rounded border p-4"
                    >
                      <p className="text-sm mb-2">
                        <span className="font-semibold">
                          {u.username}
                        </span>{' '}
                        | {u.role} | Active:{' '}
                        {u.isActive ? 'Yes' : 'No'}
                      </p>

                      <select
                        className="w-full rounded border px-3 py-2 mb-2 focus:outline-none focus:ring"
                        value={u.role}
                        onChange={e =>
                          updateUser(u.id, e.target.value, u.isActive)
                        }
                      >
                        <option value="SuperAdmin">SuperAdmin</option>
                        <option value="Admin">Admin</option>
                        <option value="Editor">Editor</option>
                      </select>

                      <div className="flex gap-2">
                        <button
                          className="rounded bg-yellow-500 px-3 py-1 text-sm text-white hover:bg-yellow-600"
                          onClick={() =>
                            updateUser(u.id, u.role, !u.isActive)
                          }
                        >
                          Toggle Active
                        </button>

                        <button
                          className="rounded bg-red-600 px-3 py-1 text-sm text-white hover:bg-red-700"
                          onClick={() => disableUser(u.id)}
                        >
                          Disable
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </>
      )}
    </div>
  )
}

export default TestApi
